import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useOrganization } from '../context/OrganizationContext';
import { supabase } from '../lib/supabase';
import ProfileModal from './ProfileModal';
import OrganizationSettingsModal from './OrganizationSettingsModal';
import { useNavigate } from 'react-router-dom';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';

export default function ProfileMenu() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { currentOrganization } = useOrganization();
  const { darkMode, toggleDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [isOrgSettingsModalOpen, setIsOrgSettingsModalOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (user) {
      loadProfile();
      loadUserRole();
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const loadUserRole = async () => {
    try {
      if (!user?.id || !currentOrganization?.id) {
        setUserRole(null);
        return;
      }

      const { data: membership, error } = await supabase
        .from('organization_members')
        .select('role')
        .eq('user_id', user.id)
        .eq('organization_id', currentOrganization.id)
        .single();

      if (error) throw error;
      console.log('User role loaded:', membership?.role);
      setUserRole(membership?.role);
    } catch (error) {
      console.error('Error loading user role:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      setIsOpen(false);
      setIsProfileModalOpen(false);
      setIsOrgSettingsModalOpen(false);
      
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-1 rounded-full hover:bg-dark-300 transition-colors"
      >
        <div className="w-8 h-8 rounded-full overflow-hidden bg-accent-primary flex items-center justify-center text-dark-100 text-sm">
          {profile?.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <span>{profile?.first_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase()}</span>
          )}
        </div>
      </button>

      {isOpen && user && (
        <div className="absolute right-0 mt-2 w-56 bg-dark-200 rounded-lg shadow-lg overflow-hidden z-[1000]">
          <div className="p-3 border-b border-dark-300">
            <div className="font-medium text-sm">{profile?.first_name} {profile?.last_name}</div>
            <div className="text-xs text-gray-400">{user?.email}</div>
          </div>

          <div className="p-2">
            <button
              onClick={() => {
                setIsProfileModalOpen(true);
                setIsOpen(false);
              }}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-300/50 dark:hover:bg-dark-300 rounded-md"
            >
              <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Profile
            </button>

            <button
              onClick={() => {
                navigate('/admin');
                setIsOpen(false);
              }}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-300/50 dark:hover:bg-dark-300 rounded-md"
            >
              <Cog6ToothIcon className="w-4 h-4 mr-3" />
              Administration
            </button>

            <button
              onClick={toggleDarkMode}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-300/50 dark:hover:bg-dark-300 rounded-md"
            >
              {darkMode ? (
                <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </button>

            <div className="border-t border-dark-300 my-2"></div>

            <button
              onClick={handleSignOut}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-300/50 dark:hover:bg-dark-300 rounded-md"
            >
              <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      )}

      {isProfileModalOpen && user && (
        <ProfileModal 
          isOpen={isProfileModalOpen} 
          onClose={() => setIsProfileModalOpen(false)}
          onProfileUpdate={loadProfile}
        />
      )}

      {isOrgSettingsModalOpen && user && (
        <OrganizationSettingsModal 
          isOpen={isOrgSettingsModalOpen} 
          onClose={() => setIsOrgSettingsModalOpen(false)}
          canEdit={userRole === 'owner' || userRole === 'admin'}
        />
      )}
    </div>
  );
} 